import { z } from "zod";

/** The most audio one transcription may carry, decoded. */
const MAX_TRANSCRIPTION_AUDIO_BYTES = 10_000_000;

const audioBytes = (base64: string) => Math.floor((base64.length * 3) / 4);

const AudioFormat = z.enum(["wav", "mp3", "flac", "m4a", "ogg", "webm", "aac"]).meta({
  id: "AudioFormat",
  description: "The audio's container format.",
});

export const TranscribeBody = z
  .strictObject({
    audio: z.strictObject({
      data: z
        .string()
        .refine(data => audioBytes(data) <= MAX_TRANSCRIPTION_AUDIO_BYTES, {
          message: `The audio is larger than ${MAX_TRANSCRIPTION_AUDIO_BYTES} bytes.`,
        })
        .meta({ description: "The audio, base64-encoded. At most 10 MB once decoded." }),
      format: AudioFormat,
    }),
    language: z
      .string()
      .regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/)
      .optional()
      .meta({ description: "The language spoken, as a BCP 47 tag.", example: "en" }),
  })
  .meta({
    id: "TranscribeBody",
    description: "Audio to turn into text.",
    example: {
      audio: {
        data: "UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=",
        format: "wav",
      },
      language: "en",
    },
  });
export type TranscribeBody = z.infer<typeof TranscribeBody>;

export const Transcription = z
  .object({
    callId: z.uuid().meta({ description: "The call, as `speech_calls` records it." }),
    text: z.string().meta({ description: "What was said." }),
    audioSeconds: z
      .number()
      .nonnegative()
      .nullable()
      .meta({ description: "The audio's length, as the vendor measured it." }),
  })
  .meta({ id: "Transcription", description: "What the audio said." });
export type Transcription = z.infer<typeof Transcription>;

/** The refusals a transcription can come back with beyond the request's shape. */
export const transcriptionRefusals = {
  PAYMENT_REQUIRED: {
    status: 402,
    message: "There aren't enough credits for this transcription.",
  },
  TRANSCRIPTION_UNAVAILABLE: {
    status: 503,
    message: "Transcription is unavailable right now.",
  },
} as const;
